import { useState, useEffect, useCallback, useMemo } from 'react';
import type { TimeEntry, NewTimeEntry, Project, BankHoliday, ISODateString, PeriodMonth, TimeEntryAuditRecord } from '../types/timesheet.types';
import { TimesheetStatus } from '../types/timesheet.types';
import type { BatchSaveResult, WorkflowSource } from '../types/connector.types';
import { WorkflowTriggerType } from '../types/connector.types';
import { useConnector } from '../contexts/ConnectorContext';
import { buildDailyTotalsMap, isDailyOverThreshold } from '../utils/calculations';
import { validateMonthSubmission } from '../utils/validation';
import { getWorkingDaysInMonth, formatISODate } from '../utils/dateUtils';
import { errorLogger } from '../utils/errorLogger';
import { SOFT_LAUNCH } from '../utils/constants';

// ─── State shape ──────────────────────────────────────────────────────────────

interface TimesheetStateInternal {
  entries: TimeEntry[];
  /** Local new entries not yet persisted to SharePoint. */
  newEntries: NewTimeEntry[];
  /** Local edits keyed by entry id (or 'new-{uuid}' for unsaved rows). */
  localEdits: Map<string, Partial<TimeEntry | NewTimeEntry>>;
  /** IDs that have been modified locally and need saving. */
  dirtyIds: Set<string>;
  /** 'projectCode::dateWorked' keys of cells explicitly cleared by the user this session. */
  clearedCells: Set<string>;
  bankHolidays: BankHoliday[];
  projects: Project[];
  isLoading: boolean;
  loadError: Error | null;
  isSaving: boolean;
  saveError: Error | null;
  lastSavedAt: Date | null;
}

export interface UseTimesheetStateResult {
  entries: (TimeEntry | NewTimeEntry)[];
  projects: Project[];
  bankHolidays: readonly BankHoliday[];
  bankHolidayDates: ReadonlySet<ISODateString>;
  dailyTotals: ReadonlyMap<ISODateString, number>;
  monthlyTotal: number;
  isDirty: boolean;
  dirtyCount: number;
  isLoading: boolean;
  loadError: Error | null;
  isSaving: boolean;
  saveError: Error | null;
  lastSavedAt: Date | null;

  /** Update a cell value locally (marks dirty). */
  updateCellHours: (entryId: string, hours: number) => void;
  /** Update task description locally (marks dirty). */
  updateCellDescription: (entryId: string, description: string) => void;
  /**
   * Create or update a (projectCode, dateWorked) cell.
   * Creates a NewTimeEntry if no matching entry exists.
   */
  upsertCellHours: (projectCode: string, dateWorked: ISODateString, hours: number | null) => void;
  /** Update description for a (projectCode, dateWorked) cell — no-op if no entry exists. */
  upsertCellDescription: (projectCode: string, dateWorked: ISODateString, description: string) => void;
  /** Get the display value of an entry (local edit takes precedence). */
  getEffectiveEntry: (id: string) => TimeEntry | NewTimeEntry | undefined;
  /** Persist all dirty rows to SharePoint. */
  save: () => Promise<BatchSaveResult>;
  /** Validate and submit the month. Returns validation errors if any. */
  submitMonth: (personId: string, userName: string, source: WorkflowSource) => Promise<string[]>;
  /** Discard all local edits. */
  discardChanges: () => void;
  /** Reload data from SharePoint. */
  refresh: () => Promise<void>;
  /** Immediately remove all entries for a project code from local state (optimistic delete). */
  removeProjectEntries: (projectCode: string) => void;

  isDayOverThreshold: (date: ISODateString) => boolean;
  isDayBankHoliday: (date: ISODateString) => boolean;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isLockedStatus(status: TimesheetStatus): boolean {
  return [
    TimesheetStatus.Submitted,
    TimesheetStatus.ManagerApproved,
    TimesheetStatus.SuperApproved,
    TimesheetStatus.Locked,
  ].includes(status);
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useTimesheetState(
  personEmail: string,
  personName: string,
  periodMonth: PeriodMonth,
  managerName = '',
  managerEmail = '',
  superApproverName = '',
  superApproverEmail = '',
  isUnlockGroup = false,
  currentUserName = '',
  currentUserEmail = ''
): UseTimesheetStateResult {
  const connector = useConnector();

  const [state, setState] = useState<TimesheetStateInternal>({
    entries: [],
    newEntries: [],
    localEdits: new Map(),
    dirtyIds: new Set(),
    clearedCells: new Set(),
    bankHolidays: [],
    projects: [],
    isLoading: true,
    loadError: null,
    isSaving: false,
    saveError: null,
    lastSavedAt: null,
  });

  // ── Load ─────────────────────────────────────────────────────────────────────

  const load = useCallback(async (background = false) => {
    if (!background) setState(s => ({ ...s, isLoading: true, loadError: null }));
    try {
      const [entries, bankHolidays, projects] = await Promise.all([
        connector.getTimeEntries({ personEmail, periodMonth }),
        connector.getBankHolidays(parseInt(periodMonth.split('-')[0])),
        connector.getProjectsForUser(personEmail, periodMonth),
      ]);
      setState(s => ({
        ...s,
        entries,
        bankHolidays,
        projects,
        // Background reload only refreshes server data — preserves any edits/new entries
        // that arrived while the async save was in flight (wiping them causes data loss).
        ...(background ? {} : {
          newEntries: [],
          localEdits: new Map(),
          dirtyIds: new Set(),
          clearedCells: new Set(),
        }),
        isLoading: false,
        loadError: null,
      }));
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useTimesheetState', action: 'load', userEmail: personEmail });
      setState(s => ({
        ...s,
        isLoading: background ? s.isLoading : false,
        loadError: error,
      }));
    }
  }, [connector, personEmail, personName, periodMonth]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Derived state ─────────────────────────────────────────────────────────────

  /** Merge server entries + new entries with local edits for display. */
  const mergedEntries = useMemo(() => {
    const all: (TimeEntry | NewTimeEntry)[] = [...state.entries, ...state.newEntries];
    if (state.localEdits.size === 0 && state.clearedCells.size === 0) return all;
    return all
      .filter(entry => !state.clearedCells.has(`${entry.projectCode}::${entry.dateWorked}`))
      .map(entry => {
        const edits = state.localEdits.get(entry.id);
        return edits ? ({ ...entry, ...edits } as TimeEntry | NewTimeEntry) : entry;
      });
  }, [state.entries, state.newEntries, state.localEdits, state.clearedCells]);

  const bankHolidayDates = useMemo<ReadonlySet<ISODateString>>(
    () => new Set(state.bankHolidays.map(bh => bh.holidayDate)),
    [state.bankHolidays]
  );

  const dailyTotals = useMemo(
    () => buildDailyTotalsMap(mergedEntries as readonly TimeEntry[]),
    [mergedEntries]
  );

  const monthlyTotal = useMemo(
    () => mergedEntries.reduce((s, e) => s + e.hours, 0),
    [mergedEntries]
  );

  // ── Mutations ─────────────────────────────────────────────────────────────────

  const updateCellHours = useCallback((entryId: string, hours: number) => {
    setState(s => {
      const edits = new Map(s.localEdits);
      edits.set(entryId, { ...(edits.get(entryId) ?? {}), hours });
      const dirty = new Set(s.dirtyIds);
      dirty.add(entryId);
      return { ...s, localEdits: edits, dirtyIds: dirty };
    });
  }, []);

  const updateCellDescription = useCallback((entryId: string, description: string) => {
    setState(s => {
      const edits = new Map(s.localEdits);
      edits.set(entryId, { ...(edits.get(entryId) ?? {}), taskDescription: description });
      const dirty = new Set(s.dirtyIds);
      dirty.add(entryId);
      return { ...s, localEdits: edits, dirtyIds: dirty };
    });
  }, []);

  const upsertCellHours = useCallback(
    (projectCode: string, dateWorked: ISODateString, hours: number | null) => {
      setState(s => {
        const cellKey = `${projectCode}::${dateWorked}`;
        const existing = [...s.entries, ...s.newEntries].find(
          e => e.projectCode === projectCode && e.dateWorked === dateWorked
        );

        if (hours === null) {
          // User cleared the cell — hide it from display
          const clearedCells = new Set(s.clearedCells);
          clearedCells.add(cellKey);
          if (existing && existing.id.startsWith('new-')) {
            // Remove unsaved entry entirely
            const newEntries = s.newEntries.filter(e => e.id !== existing.id);
            const dirtyIds = new Set(s.dirtyIds);
            dirtyIds.delete(existing.id);
            const localEdits = new Map(s.localEdits);
            localEdits.delete(existing.id);
            return { ...s, newEntries, dirtyIds, localEdits, clearedCells };
          }
          if (existing) {
            // Existing SP entry: save as 0 but hide from display via clearedCells
            const edits = new Map(s.localEdits);
            edits.set(existing.id, { ...(edits.get(existing.id) ?? {}), hours: 0 });
            const dirtyIds = new Set(s.dirtyIds);
            dirtyIds.add(existing.id);
            return { ...s, localEdits: edits, dirtyIds, clearedCells };
          }
          return { ...s, clearedCells };
        }

        // Non-null value: upsert and un-clear the cell
        const clearedCells = new Set(s.clearedCells);
        clearedCells.delete(cellKey);

        if (existing) {
          const edits = new Map(s.localEdits);
          edits.set(existing.id, { ...(edits.get(existing.id) ?? {}), hours });
          const dirty = new Set(s.dirtyIds);
          dirty.add(existing.id);
          return { ...s, localEdits: edits, dirtyIds: dirty, clearedCells };
        }
        // Create new entry
        const newId = `new-${crypto.randomUUID()}` as `new-${string}`;
        const newEntry: NewTimeEntry = {
          id: newId,
          personEmail,
          personName,
          projectCode,
          dateWorked,
          hours,
          status: TimesheetStatus.Draft,
          managerName,
          managerEmail,
          managerId: null,
          managerApprovedDate: null,
          superApproverName,
          superApproverEmail,
          superApproverId: null,
          superApprovedDate: null,
          submittedDate: null,
          lockedDate: null,
          unlockedDate: null,
          unlockReason: '',
          unlockApprovedBy: '',
          rejectionNote: '',
          billRate: null,
          costRate: null,
          taskDescription: '',
          billableToClient: false,
          assetCode: '',
        };
        const dirty = new Set(s.dirtyIds);
        dirty.add(newId);
        return { ...s, newEntries: [...s.newEntries, newEntry], dirtyIds: dirty, clearedCells };
      });
    },
    [personEmail, personName, managerName, managerEmail, superApproverName, superApproverEmail]
  );

  const upsertCellDescription = useCallback(
    (projectCode: string, dateWorked: ISODateString, description: string) => {
      setState(s => {
        const existing = [...s.entries, ...s.newEntries].find(
          e => e.projectCode === projectCode && e.dateWorked === dateWorked
        );
        if (!existing) return s;
        const edits = new Map(s.localEdits);
        edits.set(existing.id, { ...(edits.get(existing.id) ?? {}), taskDescription: description });
        const dirty = new Set(s.dirtyIds);
        dirty.add(existing.id);
        return { ...s, localEdits: edits, dirtyIds: dirty };
      });
    },
    []
  );

  const getEffectiveEntry = useCallback(
    (id: string): TimeEntry | NewTimeEntry | undefined => {
      const base = [...state.entries, ...state.newEntries].find(e => e.id === id);
      const edits = state.localEdits.get(id);
      if (!base) return undefined;
      return edits ? ({ ...base, ...edits } as TimeEntry | NewTimeEntry) : base;
    },
    [state.entries, state.newEntries, state.localEdits]
  );

  // ── Save ──────────────────────────────────────────────────────────────────────

  const save = useCallback(async (): Promise<BatchSaveResult> => {
    setState(s => ({ ...s, isSaving: true, saveError: null }));

    try {
      // Dirty existing entries (SP IDs) — stamp approver fields if not yet set
      const dirtyExisting = state.entries
        .filter(e => state.dirtyIds.has(e.id))
        .map(e => {
          const edits = state.localEdits.get(e.id);
          const merged = edits ? ({ ...e, ...edits } as TimeEntry) : e;
          return {
            ...merged,
            managerName: merged.managerName || managerName,
            managerEmail: merged.managerEmail || managerEmail,
            superApproverName: merged.superApproverName || superApproverName,
            superApproverEmail: merged.superApproverEmail || superApproverEmail,
          };
        });

      // Dirty new entries (apply local edits)
      const dirtyNew = state.newEntries
        .filter(e => state.dirtyIds.has(e.id))
        .map(e => {
          const edits = state.localEdits.get(e.id);
          return edits ? ({ ...e, ...edits } as NewTimeEntry) : e;
        });

      const allDirty: (TimeEntry | NewTimeEntry)[] = [...dirtyExisting, ...dirtyNew];

      if (allDirty.length === 0) {
        setState(s => ({ ...s, isSaving: false }));
        return { succeeded: [], failed: [] };
      }

      const result = await connector.batchSaveEntries(allDirty);

      // Capture audit records if user is in Unlock Group editing locked timesheets
      if (isUnlockGroup && dirtyExisting.length > 0) {
        // One record per person per month (not per day entry)
        const auditMap = new Map<string, TimeEntryAuditRecord>();
        for (const savedEntry of dirtyExisting) {
          const originalEntry = state.entries.find(e => e.id === savedEntry.id);
          if (originalEntry && isLockedStatus(originalEntry.status) && originalEntry.hours !== savedEntry.hours) {
            const key = `${originalEntry.managerEmail}|${savedEntry.periodMonth}`;
            if (!auditMap.has(key)) {
              auditMap.set(key, {
                timeEntryId: 0,
                personName: originalEntry.managerName || '',
                personEmail: originalEntry.managerEmail || '',
                dateWorked: `${savedEntry.periodMonth}-01` as ISODateString,
                periodMonth: savedEntry.periodMonth,
                oldValue: 'Hours modified',
                newValue: 'Hours modified',
                editorName: currentUserName,
                editorEmail: currentUserEmail,
                editedDate: formatISODate(new Date()),
                editReasonNotes: '',
              });
            }
          }
        }
        const auditRecords = [...auditMap.values()];
        if (auditRecords.length > 0) {
          try {
            await connector.batchSaveAuditRecords(auditRecords);
          } catch (err) {
            // Log but don't fail the save - audit is supplementary
            errorLogger.log(err instanceof Error ? err : new Error(String(err)), {
              component: 'useTimesheetState',
              action: 'batchSaveAuditRecords',
              userEmail: personEmail,
            });
          }
        }
      }

      // Build a lookup of saved existing entries so state.entries reflects new hours
      const savedById = new Map<string, TimeEntry>(
        dirtyExisting
          .filter(e => result.succeeded.includes(e.id as string))
          .map(e => [e.id as string, e as TimeEntry])
      );

      // Clean up succeeded existing entries; wipe all new entries (reload brings them back)
      const newEntryIds = new Set(dirtyNew.map(e => e.id));
      setState(s => {
        const dirtyIds = new Set(s.dirtyIds);
        const localEdits = new Map(s.localEdits);
        const clearedCells = new Set(s.clearedCells);
        result.succeeded.forEach(id => {
          dirtyIds.delete(id);
          localEdits.delete(id);
        });
        // Remove all new entries that were part of this save batch
        newEntryIds.forEach(id => { dirtyIds.delete(id); localEdits.delete(id); });
        const remainingNew = s.newEntries.filter(e => !newEntryIds.has(e.id));
        // Apply saved values into existing entries and immediately append newly created
        // SP entries so upsertCellHours can find them before load(true) completes.
        // Without this, editing the same cell in the async gap creates a duplicate SP item.
        const entries = [
          ...s.entries.map(e => savedById.get(e.id as string) ?? e),
          ...(result.created ?? []),
        ];
        // Clear saved entries from clearedCells
        for (const e of [...dirtyExisting, ...dirtyNew]) {
          if (result.succeeded.includes(e.id as string)) {
            clearedCells.delete(`${e.projectCode}::${e.dateWorked}`);
          }
        }
        return {
          ...s,
          entries,
          dirtyIds,
          localEdits,
          clearedCells,
          newEntries: remainingNew,
          isSaving: false,
          lastSavedAt: new Date(),
        };
      });

      // Background reload to get real SP IDs without triggering a visible refresh
      if (dirtyNew.length > 0) {
        await load(true);
      }

      return result;
    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err));
      errorLogger.log(error, { component: 'useTimesheetState', action: 'save', userEmail: personEmail });
      setState(s => ({ ...s, isSaving: false, saveError: error }));
      throw error;
    }
  }, [connector, state.entries, state.newEntries, state.dirtyIds, state.localEdits, load,
      managerName, managerEmail, superApproverName, superApproverEmail, isUnlockGroup, currentUserName, currentUserEmail]);

  // ── Submit month ──────────────────────────────────────────────────────────────

  const submitMonth = useCallback(
    async (pId: string, userName: string, source: WorkflowSource): Promise<string[]> => {
      try {
      // STEP 1: Validate using current merged entries (local edits included, before save clears them).
      const allWorkingDays = getWorkingDaysInMonth(periodMonth);
      const validation = validateMonthSubmission(
        mergedEntries as readonly TimeEntry[],
        periodMonth,
        bankHolidayDates,
        allWorkingDays
      );
      if (!validation.valid) return validation.errors;

      // STEP 2: Persist all dirty entries to SharePoint.
      if (state.dirtyIds.size > 0) {
        await save();
      }

      // STEP 3: Fetch fresh entries so newly created rows have real SP IDs.
      // (Entries created in this session were saved above with new-{uuid} IDs;
      // the fresh fetch returns them with their actual SharePoint item IDs.)
      const freshEntries = await connector.getTimeEntries({ personEmail, periodMonth });

      // STEP 4: Batch-update all submittable entries to Submitted.
      const submittedDate = formatISODate(new Date());
      const toSubmit: TimeEntry[] = freshEntries
        .filter(e => e.status === TimesheetStatus.Draft || e.status === TimesheetStatus.Rejected)
        .map(e => ({
          ...e,
          status: TimesheetStatus.Submitted,
          submittedDate,
          managerName: e.managerName || managerName,
          managerEmail: e.managerEmail || managerEmail,
          superApproverName: e.superApproverName || superApproverName,
          superApproverEmail: e.superApproverEmail || superApproverEmail,
        }));

      if (toSubmit.length > 0) {
        const { failed } = await connector.batchSaveEntries(toSubmit);
        if (failed.length > 0) {
          const details = failed.map(f => f.error).join('; ');
          throw new Error(`Failed to save ${failed.length} timesheet entr${failed.length > 1 ? 'ies' : 'y'}: ${details}`);
        }
      }

      await connector.triggerWorkflow({
        triggerType: WorkflowTriggerType.SubmitMonth,
        personId: pId,
        userName,
        userEmail: personEmail,
        periodMonth,
        actionStatus: 'Submitted',
        isFinalState: false,
        approverName: SOFT_LAUNCH.enabled ? SOFT_LAUNCH.approverName : managerName,
        approverEmail: SOFT_LAUNCH.enabled ? SOFT_LAUNCH.approverEmail : managerEmail,
        approverRole: SOFT_LAUNCH.enabled ? 'Super Approver' : 'Manager',
        source,
        totalHours: monthlyTotal,
      });

      // STEP 5: Update local state to reflect submitted status and clear dirty tracking.
      setState(s => ({
        ...s,
        entries: freshEntries.map(e => ({
          ...e,
          status: TimesheetStatus.Submitted,
          submittedDate,
          managerName: e.managerName || managerName,
          managerEmail: e.managerEmail || managerEmail,
          superApproverName: e.superApproverName || superApproverName,
          superApproverEmail: e.superApproverEmail || superApproverEmail,
        })),
        newEntries: [],
        localEdits: new Map(),
        dirtyIds: new Set(),
      }));

      return [];
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        errorLogger.log(error, { component: 'useTimesheetState', action: 'submitMonth', userEmail: personEmail });
        throw error;
      }
    },
    [connector, personEmail, state.dirtyIds.size, mergedEntries, monthlyTotal, periodMonth,
     bankHolidayDates, save, managerName, managerEmail, superApproverName, superApproverEmail]
  );

  // ── Remove project entries (optimistic delete) ────────────────────────────────

  const removeProjectEntries = useCallback((projectCode: string) => {
    setState(s => {
      const toRemove = new Set([
        ...s.entries.filter(e => e.projectCode === projectCode).map(e => e.id as string),
        ...s.newEntries.filter(e => e.projectCode === projectCode).map(e => e.id),
      ]);
      const entries = s.entries.filter(e => e.projectCode !== projectCode);
      const newEntries = s.newEntries.filter(e => e.projectCode !== projectCode);
      const dirtyIds = new Set(s.dirtyIds);
      const localEdits = new Map(s.localEdits);
      toRemove.forEach(id => { dirtyIds.delete(id); localEdits.delete(id); });
      const clearedCells = new Set([...s.clearedCells].filter(k => !k.startsWith(`${projectCode}::`)));
      return { ...s, entries, newEntries, dirtyIds, localEdits, clearedCells };
    });
  }, []);

  // ── Discard ───────────────────────────────────────────────────────────────────

  const discardChanges = useCallback(() => {
    setState(s => ({ ...s, localEdits: new Map(), dirtyIds: new Set(), newEntries: [], clearedCells: new Set() }));
  }, []);

  // ── Helpers ───────────────────────────────────────────────────────────────────

  const isDayOverThreshold = useCallback(
    (date: ISODateString) => isDailyOverThreshold(mergedEntries as readonly TimeEntry[], date),
    [mergedEntries]
  );

  const isDayBankHoliday = useCallback(
    (date: ISODateString) => bankHolidayDates.has(date),
    [bankHolidayDates]
  );

  return {
    entries: mergedEntries,
    projects: state.projects,
    bankHolidays: state.bankHolidays,
    bankHolidayDates,
    dailyTotals,
    monthlyTotal,
    isDirty: state.dirtyIds.size > 0,
    dirtyCount: state.dirtyIds.size,
    isLoading: state.isLoading,
    loadError: state.loadError,
    isSaving: state.isSaving,
    saveError: state.saveError,
    lastSavedAt: state.lastSavedAt,
    updateCellHours,
    updateCellDescription,
    upsertCellHours,
    upsertCellDescription,
    getEffectiveEntry,
    save,
    submitMonth,
    discardChanges,
    refresh: load,
    removeProjectEntries,
    isDayOverThreshold,
    isDayBankHoliday,
  };
}
